/**
 * The playout view's model (PLAN §5.2, §7.4, D3): the running order —
 * rundowns → segments → parts → pieces, read from the definition rows — with
 * the playout state laid over it: which part is On Air and which is Next,
 * the on-air countdown, each segment's budget against its pace, and each
 * part's pieces placed on their source-layer lanes.
 *
 * **Instances win over definitions where they exist** (§5.2): the on-air and
 * next parts show their INSTANCE's title and pieces — what is actually
 * playing, adlibs included — while every other part shows its definition. An
 * instance whose definition is gone (deleted on air, or a queued adlib part)
 * is still shown, marked, where it plays.
 *
 * Pure: the timings passed in are already advanced to "now" (`clock.ts`).
 */
import type { PartInstanceState, PlayoutState } from '@acp/api-contract/playout.js';
import { expandFieldPaths, partFieldsSchema, pieceFieldsSchema, segmentFieldsSchema, type PartFields, type PieceFields, type SegmentFields } from '@acp/domain/fields.js';
import type { PieceInstance } from '@acp/domain/instances.js';
import type { Row } from '@acp/domain/row.js';
import { childrenOf, type RowTree } from '@acp/domain/tree.js';

/** One rundown of the production, as loaded (its head row's name and its materialised rows). */
export type RundownSource = { readonly rundownId: string; readonly name: string; readonly tree: RowTree };

/** A source layer as the studio config names it (`sourceLayers`, D22), in the config's order. */
export type SourceLayer = { readonly id: string; readonly label: string };

export type PartStatus = 'onair' | 'next' | 'played' | 'upcoming';

export type PieceView = {
  readonly id: string;
  readonly name: string;
  readonly pieceType: string;
  readonly layer: string;
  /** From the part's start, ms. */
  readonly startMs: number;
  /** Null: until something ends it (open-ended). */
  readonly durationMs: number | null;
  /** Played into the running part as an adlib. */
  readonly adlib: boolean;
  /** Skipped (`disableNextPiece`). */
  readonly disabled: boolean;
  /** Stopped early by an operator (`stopPiecesOnLayer`). */
  readonly stopped: boolean;
};

export type LaneView = { readonly id: string; readonly label: string; readonly pieces: readonly PieceView[] };

export type PartView = {
  readonly partId: string;
  readonly segmentId: string;
  readonly title: string;
  readonly status: PartStatus;
  /** The part's running instance, when it is on air or next. */
  readonly instanceId: string | null;
  /** The instance plays without a definition: deleted on air, or a queued adlib part (§5.2). */
  readonly orphaned: 'deleted' | 'adlib-part' | null;
  /** Marked invalid in the rundown: not playable (skipped by the running order). */
  readonly invalid: boolean;
  readonly expectedDurationMs: number | null;
  readonly autoNext: boolean;
  readonly untimed: boolean;
  /** Only the lanes this part uses, in the studio's lane order. */
  readonly lanes: readonly LaneView[];
  /** The length the piece strip spans, ms: the expected duration, or the pieces' extent. */
  readonly spanMs: number;
  /** How long it played last time, when it has played in this activation. */
  readonly playedMs: number | null;
};

export type SegmentView = {
  readonly segmentId: string;
  readonly rundownId: string;
  readonly name: string;
  readonly hidden: boolean;
  readonly parts: readonly PartView[];
  /** The segment's budget (`budgetDuration`), null for none. */
  readonly budgetMs: number | null;
  /** The planned length of its timed parts. */
  readonly expectedMs: number;
  /** Played so far in this activation (the on-air part so far included). */
  readonly playedMs: number;
  /** Over (+) / under (−) its budget at the current pace; null without a budget. */
  readonly overUnderMs: number | null;
  /** `queueNextSegment` named it. */
  readonly queued: boolean;
  readonly containsOnAir: boolean;
  readonly containsNext: boolean;
  /** Synthetic: instances playing outside the running order. */
  readonly offRunningOrder: boolean;
};

export type RundownView = { readonly rundownId: string; readonly name: string; readonly segments: readonly SegmentView[] };

export type PlayoutView = {
  readonly rundowns: readonly RundownView[];
  readonly onAirPartId: string | null;
  readonly nextPartId: string | null;
  /** Every part id in running order (keyboard navigation walks it). */
  readonly partOrder: readonly string[];
  /** Part → its segment, for the segment commands. */
  readonly segmentOf: ReadonlyMap<string, string>;
};

/** The shortest strip span: a part with nothing timed still gets a readable strip. */
export const MIN_SPAN_MS = 10_000;

/** The segment that holds instances playing outside the running order. */
export const OFF_RUNNING_ORDER_ID = 'off-running-order';

type Ctx = {
  readonly state: PlayoutState | null;
  readonly layers: readonly SourceLayer[];
  /** The latest history entry per part: how long it played. */
  readonly played: ReadonlyMap<string, number>;
};

function pieceFields(row: Row): PieceFields | null {
  const parsed = pieceFieldsSchema.safeParse(expandFieldPaths(row.f));
  return parsed.success ? parsed.data : null;
}

/** A part's fields; stored data that fails its schema reads as an invalid part (PLAN §6.4), never a crash. */
export function partFieldsOf(row: Row | null): PartFields {
  const parsed = partFieldsSchema.safeParse(expandFieldPaths(row?.f ?? {}));
  return parsed.success ? parsed.data : { ...partFieldsSchema.parse({}), invalid: true, invalidReason: 'its stored fields fail the schema' };
}

function segmentFieldsOf(row: Row): SegmentFields {
  const parsed = segmentFieldsSchema.safeParse(expandFieldPaths(row.f));
  return parsed.success ? parsed.data : segmentFieldsSchema.parse({});
}

function definitionPiece(row: Row, fields: PieceFields): PieceView {
  return {
    id: row.rowId,
    name: fields.name,
    pieceType: fields.pieceType,
    layer: fields.sourceLayer,
    startMs: fields.enable.start === 'now' ? 0 : fields.enable.start,
    durationMs: fields.enable.duration,
    adlib: false,
    disabled: false,
    stopped: false,
  };
}

/** Where a piece instance sits in its part: from its planned start once the part started, else its own enable. */
function instancePiece(p: PieceInstance, partStartedAt: number | null): PieceView {
  const own = p.piece.enable.start === 'now' ? 0 : p.piece.enable.start;
  const plannedStart = p.plannedStartedPlayback ?? p.dynamicallyInserted?.time ?? null;
  const startMs = partStartedAt !== null && plannedStart !== null ? Math.max(0, plannedStart - partStartedAt) : own;
  const stopped = p.userStopped !== null;
  const stopAt = partStartedAt !== null && p.userStopped !== null ? Math.max(startMs, p.userStopped - partStartedAt) : null;
  return {
    id: p.instanceId,
    name: p.piece.name,
    pieceType: p.piece.pieceType,
    layer: p.piece.sourceLayer,
    startMs,
    durationMs: stopAt !== null ? stopAt - startMs : p.piece.enable.duration,
    adlib: p.dynamicallyInserted !== null,
    disabled: p.disabled,
    stopped,
  };
}

/** The lanes a part's pieces use, in the studio's order; layers the config does not name follow, by id. */
export function lanesOf(pieces: readonly PieceView[], layers: readonly SourceLayer[]): LaneView[] {
  const byLayer = new Map<string, PieceView[]>();
  for (const piece of pieces) byLayer.set(piece.layer, [...(byLayer.get(piece.layer) ?? []), piece]);
  const known = layers.filter((l) => byLayer.has(l.id));
  const unknown = [...byLayer.keys()].filter((id) => !layers.some((l) => l.id === id)).sort();
  return [...known, ...unknown.map((id) => ({ id, label: id === '' ? '(no layer)' : id }))].map((l) => ({ ...l, pieces: byLayer.get(l.id) ?? [] }));
}

function spanOf(expectedMs: number | null, pieces: readonly PieceView[], elapsedMs: number | null): number {
  const extent = pieces.reduce((max, p) => Math.max(max, p.startMs + (p.durationMs ?? 0)), 0);
  return Math.max(expectedMs ?? 0, extent, elapsedMs ?? 0, MIN_SPAN_MS);
}

function statusOf(ctx: Ctx, partId: string): PartStatus {
  if (ctx.state?.current?.instance.partId === partId) return 'onair';
  if (ctx.state?.next?.instance.partId === partId) return 'next';
  return ctx.played.has(partId) ? 'played' : 'upcoming';
}

function instanceFor(ctx: Ctx, status: PartStatus): PartInstanceState | null {
  if (status === 'onair') return ctx.state?.current ?? null;
  if (status === 'next') return ctx.state?.next ?? null;
  return null;
}

/** A part from its instance (on air / next) or its definition row. */
function partView(ctx: Ctx, partId: string, segmentId: string, row: Row | null, pieceRows: readonly Row[], status: PartStatus): PartView {
  const instance = instanceFor(ctx, status);
  const fields = instance?.instance.part ?? partFieldsOf(row);
  const startedAt = instance?.instance.timings.reportedStartedPlayback ?? null;
  const pieces =
    instance !== null
      ? instance.pieces.map((p) => instancePiece(p, startedAt))
      : pieceRows.flatMap((r) => {
          if (r.kind !== 'piece') return [];
          const f = pieceFields(r);
          return f === null ? [] : [definitionPiece(r, f)];
        });
  const elapsed = status === 'onair' ? (ctx.state?.timing.onAir?.elapsedMs ?? null) : null;
  return {
    partId,
    segmentId,
    title: fields.title === '' ? '(untitled part)' : fields.title,
    status,
    instanceId: instance?.instance.instanceId ?? null,
    orphaned: instance?.instance.orphaned ?? null,
    invalid: fields.invalid,
    expectedDurationMs: fields.expectedDuration,
    autoNext: fields.autoNext,
    untimed: fields.untimed,
    lanes: lanesOf(pieces, ctx.layers),
    spanMs: spanOf(fields.expectedDuration, pieces, elapsed),
    playedMs: ctx.played.get(partId) ?? null,
  };
}

/** Played time and pace of one segment (the server's figures when it gives them, §7.4). */
function segmentTiming(ctx: Ctx, segmentId: string, parts: readonly PartView[], budgetMs: number | null) {
  const expectedMs = parts.reduce((sum, p) => (p.untimed ? sum : sum + (p.expectedDurationMs ?? 0)), 0);
  const server = ctx.state?.timing.segments.find((s) => s.segmentId === segmentId);
  const onAir = ctx.state?.timing.onAir ?? null;
  const onAirHere = parts.find((p) => p.status === 'onair');
  const historyMs = (ctx.state?.history ?? []).reduce((sum, h) => (h.segmentId === segmentId && !h.untimed ? sum + (h.stoppedAt - h.startedAt) : sum), 0);
  const playedMs = server?.playedMs ?? historyMs + (onAirHere !== undefined && !onAirHere.untimed && onAir !== null ? onAir.elapsedMs : 0);
  if (server !== undefined) return { expectedMs, playedMs, budgetMs: server.budgetMs ?? budgetMs, overUnderMs: server.budgetMs === null && budgetMs === null ? null : server.overUnderMs };
  if (budgetMs === null) return { expectedMs, playedMs, budgetMs, overUnderMs: null };
  // At the current pace: what has played, what the on-air part still needs, and every part still to come.
  const onAirLeft = onAirHere !== undefined && onAir !== null ? Math.max(0, onAir.remainingMs ?? 0) : 0;
  const toCome = parts.reduce((sum, p) => (p.untimed || (p.status !== 'upcoming' && p.status !== 'next') ? sum : sum + (p.expectedDurationMs ?? 0)), 0);
  return { expectedMs, playedMs, budgetMs, overUnderMs: playedMs + onAirLeft + toCome - budgetMs };
}

type SegmentHead = Pick<SegmentView, 'segmentId' | 'rundownId' | 'name' | 'hidden' | 'budgetMs' | 'offRunningOrder'>;

function segmentView(ctx: Ctx, head: SegmentHead, parts: readonly PartView[]): SegmentView {
  return {
    ...head,
    parts,
    ...segmentTiming(ctx, head.segmentId, parts, head.budgetMs),
    queued: ctx.state?.queuedSegmentId === head.segmentId,
    containsOnAir: parts.some((p) => p.status === 'onair'),
    containsNext: parts.some((p) => p.status === 'next'),
    offRunningOrder: head.offRunningOrder,
  };
}

function rundownView(ctx: Ctx, source: RundownSource): RundownView {
  const segments = childrenOf(source.tree, source.rundownId).flatMap((segment) => {
    if (segment.kind !== 'segment') return [];
    const f = segmentFieldsOf(segment);
    const parts = childrenOf(source.tree, segment.rowId).flatMap((row) =>
      row.kind === 'part' ? [partView(ctx, row.rowId, segment.rowId, row, childrenOf(source.tree, row.rowId), statusOf(ctx, row.rowId))] : [],
    );
    const head = { segmentId: segment.rowId, rundownId: source.rundownId, name: f.name === '' ? '(unnamed segment)' : f.name, hidden: f.hidden, budgetMs: f.budgetDuration, offRunningOrder: false };
    return [segmentView(ctx, head, parts)];
  });
  return { rundownId: source.rundownId, name: source.name, segments };
}

/** Where an orphan plays in a segment: right after the on-air part (a queued adlib part), at the end of its own segment, or not here. */
function orphanIndex(segment: SegmentView, orphan: PartView): number | null {
  if (orphan.orphaned === 'adlib-part') {
    const onAir = segment.parts.findIndex((p) => p.status === 'onair');
    if (onAir >= 0) return onAir + 1;
  }
  return segment.segmentId === orphan.segmentId ? segment.parts.length : null;
}

/** The rundowns with `orphan` inserted into the first segment that places it, or null when none does. */
function insertOrphan(ctx: Ctx, rundowns: readonly RundownView[], orphan: PartView): RundownView[] | null {
  for (const [ri, r] of rundowns.entries()) {
    for (const [si, s] of r.segments.entries()) {
      const at = orphanIndex(s, orphan);
      if (at === null) continue;
      const segment = segmentView(ctx, s, [...s.parts.slice(0, at), orphan, ...s.parts.slice(at)]);
      const segments = r.segments.map((x, i) => (i === si ? segment : x));
      return rundowns.map((x, i) => (i === ri ? { ...r, segments } : x));
    }
  }
  return null;
}

/** Insert the on-air/next instances that no definition row shows, where they play (§5.2: an orphan keeps playing). */
function placeOrphans(ctx: Ctx, rundowns: RundownView[]): RundownView[] {
  const shown = new Set(rundowns.flatMap((r) => r.segments.flatMap((s) => s.parts.map((p) => p.partId))));
  const orphans = (['onair', 'next'] as const).flatMap((status) => {
    const inst = instanceFor(ctx, status);
    return inst === null || shown.has(inst.instance.partId) ? [] : [partView(ctx, inst.instance.partId, inst.instance.segmentId, null, [], status)];
  });
  let out = rundowns;
  const homeless: PartView[] = [];
  for (const orphan of orphans) {
    const placed = insertOrphan(ctx, out, orphan);
    if (placed === null) homeless.push(orphan);
    else out = placed;
  }
  if (homeless.length === 0) return out;
  const last = out.at(-1);
  const head = { segmentId: OFF_RUNNING_ORDER_ID, rundownId: last?.rundownId ?? '', name: 'Not in the running order', hidden: false, budgetMs: null, offRunningOrder: true };
  const extra = segmentView(ctx, head, homeless);
  return last === undefined ? [{ rundownId: '', name: '', segments: [extra] }] : [...out.slice(0, -1), { ...last, segments: [...last.segments, extra] }];
}

/** The whole view: the running order with the playout state laid over it. */
export function playoutView(rundowns: readonly RundownSource[], state: PlayoutState | null, layers: readonly SourceLayer[]): PlayoutView {
  const played = new Map<string, number>();
  for (const h of state?.history ?? []) played.set(h.partId, h.stoppedAt - h.startedAt);
  const ctx: Ctx = { state, layers, played };
  const views = placeOrphans(ctx, rundowns.map((r) => rundownView(ctx, r)));
  const partOrder: string[] = [];
  const segmentOf = new Map<string, string>();
  for (const r of views) {
    for (const s of r.segments) {
      for (const p of s.parts) {
        partOrder.push(p.partId);
        segmentOf.set(p.partId, s.segmentId);
      }
    }
  }
  return { rundowns: views, onAirPartId: state?.current?.instance.partId ?? null, nextPartId: state?.next?.instance.partId ?? null, partOrder, segmentOf };
}

/** The source layers of a studio config's `sourceLayers`, in its order (labels default to the id). */
export function sourceLayersOf(sourceLayers: Readonly<Record<string, { readonly label: string }>>): SourceLayer[] {
  return Object.entries(sourceLayers).map(([id, l]) => ({ id, label: l.label === '' ? id : l.label }));
}
